/**
 * v13+ side-menu HTML builder for the Edit Satellite plugin.
 *
 * Pure string builders (no `this`), split out of edit-sat.ts so the plugin class
 * stays a thin wiring layer. The element IDs produced here are the contract the
 * plugin's event wiring and tests depend on, so keep them stable when editing the
 * markup. The menu is the body only: the `editSat-menu` side-menu-parent wrapper
 * is generated by the side-menu pipeline, and the plugin adds the `kt-ui-v13`
 * marker to it in `uiManagerFinal_`.
 */
import { html } from '@app/engine/utils/development/formatter';
import { t7e } from '@app/locales/keys';

type T7eKey = Parameters<typeof t7e>[0];

/** Shared element-ID prefix (matches `EditSat.elementPrefix`). */
const P = 'es';

const l = (key: string): string => t7e(`plugins.EditSat.labels.${key}` as T7eKey);
const b = (key: string): string => t7e(`plugins.EditSat.buttons.${key}` as T7eKey);
const s = (key: string): string => t7e(`plugins.EditSat.sections.${key}` as T7eKey);

/** A full-width v13 action row (label left, chevron added via CSS). */
export function editSatActionButton(id: string, label: string, opts: { submit?: boolean } = {}): string {
  const type = opts.submit ? 'submit' : 'button';

  return html`
    <button id="${id}" type="${type}" class="kt-action waves-effect">
      <span class="kt-action-label">${label}</span>
    </button>
  `;
}

/** Build the Edit Satellite side-menu body (v13+ "FAANG card" layout). */
export function buildEditSatMenuHtml(): string {
  return html`
    <form id="editSat-menu-form">
      <div id="${P}-error" class="kt-note start-hidden menu-selectable">
        <span id="${P}-error-msg">${l('error')}</span>
      </div>

      <section class="kt-section">
        <div class="kt-section-label">${s('identity')}</div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input disabled value="AAAAA" id="${P}-scc" type="text" maxlength="9" />
            <label for="${P}-scc" class="active">${l('scc')}</label>
          </div>
          <div class="input-field col s6">
            <input placeholder="${t7e('Common.unknown')}" id="${P}-country" type="text" />
            <label for="${P}-country" class="active">${l('country')}</label>
          </div>
        </div>
      </section>

      <section class="kt-section">
        <div class="kt-section-label">${s('epoch')}</div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input placeholder="AA" id="${P}-year" type="text" maxlength="2" />
            <label for="${P}-year" class="active">${l('epochYear')}</label>
          </div>
          <div class="input-field col s6">
            <input placeholder="AAA.AAAAAAAA" id="${P}-day" type="text" maxlength="12" />
            <label for="${P}-day" class="active">${l('epochDay')}</label>
          </div>
        </div>
      </section>

      <section class="kt-section">
        <div class="kt-section-label">${s('elements')}</div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input placeholder="AAA.AAAA" id="${P}-inc" type="text" maxlength="8" />
            <label for="${P}-inc" class="active">${l('inclination')}</label>
          </div>
          <div class="input-field col s6">
            <input placeholder="AAA.AAAA" id="${P}-rasc" type="text" maxlength="8" />
            <label for="${P}-rasc" class="active">${l('rightAscension')}</label>
          </div>
        </div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input placeholder="AAAAAAA" id="${P}-ecen" type="text" maxlength="7" />
            <label for="${P}-ecen" class="active">${l('eccentricity')}</label>
          </div>
          <div class="input-field col s6">
            <input placeholder="AA.AAAAAAAA" id="${P}-argPe" type="text" maxlength="8" />
            <label for="${P}-argPe" class="active">${l('argOfPerigee')}</label>
          </div>
        </div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input placeholder="AAA.AAAA" id="${P}-meana" type="text" maxlength="8" />
            <label for="${P}-meana" class="active">${l('meanAnomaly')}</label>
          </div>
          <div class="input-field col s6">
            <input placeholder="AA.AAAAA" id="${P}-meanmo" type="text" maxlength="11" />
            <label for="${P}-meanmo" class="active">${l('meanMotion')}</label>
          </div>
        </div>
        <div class="kt-field-row">
          <div class="input-field col s12">
            <input placeholder="" id="${P}-per" type="text" maxlength="11" />
            <label for="${P}-per" class="active">${l('period')}</label>
          </div>
        </div>
      </section>

      <section class="kt-section" id="${P}-derived">
        <div class="kt-section-label">${s('calculated')}</div>
        <div class="kt-field-row">
          <div class="input-field col s6">
            <input disabled id="${P}-calc-apogee" type="text" />
            <label for="${P}-calc-apogee" class="active">${l('apogeeKm')}</label>
          </div>
          <div class="input-field col s6">
            <input disabled id="${P}-calc-perigee" type="text" />
            <label for="${P}-calc-perigee" class="active">${l('perigeeKm')}</label>
          </div>
        </div>
        <div class="kt-field-row">
          <div class="input-field col s12">
            <input disabled id="${P}-calc-period" type="text" />
            <label for="${P}-calc-period" class="active">${l('periodMin')}</label>
          </div>
        </div>
      </section>

      <section class="kt-section">
        <div class="kt-section-label">${s('actions')}</div>
        ${editSatActionButton('editSat-submit', b('updateSatellite'), { submit: true })}
        ${editSatActionButton('editSat-newTLE', b('updateEpoch'))}
        ${editSatActionButton('editSat-reset', b('resetOriginal'))}
        <div class="kt-divider"></div>
        ${editSatActionButton('editSat-save', b('saveTle'))}
        ${editSatActionButton('editSat-open', b('loadTle'))}
        <input id="editSat-file" class="start-hidden" type="file" name="files[]" />
      </section>
    </form>
  `;
}
